/**
 * CostMetricsCard - Platform cost monitoring dashboard card (Task 080)
 *
 * Displays:
 * - Total platform cost for the selected period
 * - Cost change vs previous period
 * - Cost breakdown by workflow step
 * - Average cost per request and user
 * - Top users by cost
 */

import React from 'react';
import { Card, CardSkeleton, Button } from '@/components/ui';
import type {
  CostSummary,
  CostTrendsResponse,
  TopCostUsersResponse,
  CostPeriod,
} from '@/services/admin/AdminService';

interface CostMetricsCardProps {
  costSummary: CostSummary | null;
  costTrends: CostTrendsResponse | null;
  topUsers: TopCostUsersResponse | null;
  loading: boolean;
  period: CostPeriod;
  onPeriodChange: (period: CostPeriod) => void;
  onRefresh: () => void;
}

// Format USD with proper precision
const formatCost = (value: number): string => {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
};

// Format large numbers
const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

// Dollar icon
const DollarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-5 h-5'}
  >
    <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.421z" />
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z"
      clipRule="evenodd"
    />
  </svg>
);

// Period selector button group
const PeriodSelector: React.FC<{
  period: CostPeriod;
  onPeriodChange: (period: CostPeriod) => void;
}> = ({ period, onPeriodChange }) => (
  <div className="inline-flex rounded-lg border border-secondary-200 bg-white p-1">
    {(['day', 'week', 'month'] as CostPeriod[]).map((p) => (
      <button
        key={p}
        onClick={() => onPeriodChange(p)}
        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
          period === p
            ? 'bg-primary-100 text-primary-700'
            : 'text-secondary-600 hover:bg-secondary-50'
        }`}
      >
        {p.charAt(0).toUpperCase() + p.slice(1)}
      </button>
    ))}
  </div>
);

// Change indicator
const ChangeIndicator: React.FC<{ change: number | null }> = ({ change }) => {
  if (change === null) return null;

  const isPositive = change > 0;
  const isNeutral = change === 0;

  return (
    <span
      className={`text-sm font-medium ${
        isNeutral
          ? 'text-secondary-500'
          : isPositive
            ? 'text-red-600'
            : 'text-green-600'
      }`}
    >
      {isPositive ? '+' : ''}
      {change.toFixed(1)}% vs prev
    </span>
  );
};

// Step cost breakdown
const StepBreakdown: React.FC<{ costByStep: Record<string, number> }> = ({
  costByStep,
}) => {
  const sortedSteps = Object.entries(costByStep)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  const total = Object.values(costByStep).reduce((sum, v) => sum + v, 0);

  const stepLabels: Record<string, string> = {
    discovery: 'Discovery',
    design: 'Design',
    multiagent: 'Architecture',
    technical: 'Technical',
    codegen: 'Code Gen',
    operations: 'Operations',
  };

  return (
    <div className="space-y-2">
      {sortedSteps.map(([step, cost]) => {
        const percentage = total > 0 ? (cost / total) * 100 : 0;
        return (
          <div key={step} className="flex items-center">
            <span className="w-24 text-sm text-secondary-600 truncate">
              {stepLabels[step] || step}
            </span>
            <div className="flex-1 mx-3">
              <div className="h-2 bg-secondary-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
            <span className="w-16 text-sm text-secondary-700 text-right">
              {formatCost(cost)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Top users list
const TopUsersList: React.FC<{ topUsers: TopCostUsersResponse }> = ({
  topUsers,
}) => {
  if (!topUsers.users.length) {
    return (
      <p className="text-sm text-secondary-500">No user cost data available</p>
    );
  }

  return (
    <div className="space-y-2">
      {topUsers.users.slice(0, 5).map((user, index) => (
        <div
          key={user.user_id}
          className="flex items-center justify-between py-1"
        >
          <div className="flex items-center">
            <span className="w-5 text-sm text-secondary-400">{index + 1}.</span>
            <span className="text-sm text-secondary-700 truncate max-w-[150px]">
              {user.email || user.user_id.slice(0, 8)}
            </span>
          </div>
          <div className="text-right">
            <span className="text-sm font-medium text-secondary-900">
              {formatCost(user.total_cost_usd)}
            </span>
            <span className="text-xs text-secondary-500 ml-2">
              ({user.total_requests} reqs)
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const CostMetricsCard: React.FC<CostMetricsCardProps> = ({
  costSummary,
  costTrends,
  topUsers,
  loading,
  period,
  onPeriodChange,
  onRefresh,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <DollarIcon className="w-6 h-6 mr-2 text-primary-600" />
          <h3 className="text-lg font-semibold text-secondary-900">
            Platform Costs
          </h3>
        </div>
        <div className="flex items-center space-x-3">
          <PeriodSelector period={period} onPeriodChange={onPeriodChange} />
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </div>

      {!costSummary ? (
        <div className="text-center py-8">
          <p className="text-secondary-500">
            No cost data available yet. Costs will appear once users start
            designing agents.
          </p>
        </div>
      ) : (
        <>
          {/* Main metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-primary-50 rounded-lg">
              <p className="text-3xl font-bold text-primary-600">
                {formatCost(costSummary.metrics.total_cost_usd)}
              </p>
              <p className="text-sm font-medium text-secondary-700">
                Total Cost
              </p>
              <ChangeIndicator change={costSummary.cost_change_percent} />
            </div>
            <div className="text-center p-4">
              <p className="text-2xl font-bold text-secondary-900">
                {formatNumber(costSummary.metrics.total_requests)}
              </p>
              <p className="text-sm text-secondary-600">Total Requests</p>
            </div>
            <div className="text-center p-4">
              <p className="text-2xl font-bold text-secondary-900">
                {formatCost(costSummary.metrics.avg_cost_per_request)}
              </p>
              <p className="text-sm text-secondary-600">Avg/Request</p>
            </div>
            <div className="text-center p-4">
              <p className="text-2xl font-bold text-secondary-900">
                {formatCost(costSummary.metrics.avg_cost_per_user)}
              </p>
              <p className="text-sm text-secondary-600">Avg/User</p>
            </div>
          </div>

          {/* Token usage */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border border-secondary-200 rounded-lg p-4">
              <p className="text-sm text-secondary-600 mb-1">Input Tokens</p>
              <p className="text-xl font-semibold text-secondary-900">
                {formatNumber(costSummary.metrics.total_input_tokens)}
              </p>
            </div>
            <div className="border border-secondary-200 rounded-lg p-4">
              <p className="text-sm text-secondary-600 mb-1">Output Tokens</p>
              <p className="text-xl font-semibold text-secondary-900">
                {formatNumber(costSummary.metrics.total_output_tokens)}
              </p>
            </div>
          </div>

          {/* Projected cost if we have trends */}
          {costTrends?.projected_monthly_cost && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Projected Monthly Cost:</span>{' '}
                {formatCost(costTrends.projected_monthly_cost)}
                <span className="text-blue-600 ml-2">
                  (based on {formatCost(costTrends.avg_daily_cost)}/day avg)
                </span>
              </p>
            </div>
          )}

          {/* Two column layout for breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cost by step */}
            <div className="border border-secondary-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-secondary-700 mb-3">
                Cost by Workflow Step
              </h4>
              {Object.keys(costSummary.metrics.cost_by_step).length > 0 ? (
                <StepBreakdown costByStep={costSummary.metrics.cost_by_step} />
              ) : (
                <p className="text-sm text-secondary-500">No step data yet</p>
              )}
            </div>

            {/* Top users */}
            <div className="border border-secondary-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-secondary-700 mb-3">
                Top Users by Cost
              </h4>
              {topUsers ? (
                <TopUsersList topUsers={topUsers} />
              ) : (
                <p className="text-sm text-secondary-500">No user data yet</p>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default CostMetricsCard;
